import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
  AccordionItemState,
} from "react-accessible-accordion";

import { ReactComponent as ChevronDown } from "assets/images/iconsV2/chevron-down-tertiary.svg";
import testIds from "constants/testIds";
import { classNames } from "helpers/commonHelpers";

const SECTION_TEST_IDS: Record<string, string> = {
  "request-form.organisation": testIds.REQUEST_FORM_ORGANISATION_SECTION,
  "request-form.service": testIds.REQUEST_FORM_SERVICE_SECTION,
  "request-form.schedule": testIds.REQUEST_FORM_SCHEDULE_SECTION,
  "request-form.packingList": testIds.REQUEST_FORM_PACKING_LIST_SECTION,
  "request-form.provider": testIds.REQUEST_FORM_PROVIDER_SECTION,
};

interface RequestFormAccordionProps {
  uuid: string;
  title: string;
  badge?: React.ReactNode;
  subHeaderBadge?: React.ReactNode;
  disabled?: boolean;
  onBlur?: () => void;
  children: React.ReactNode;
}

interface AccordionPanelContentProps {
  isExpanded: boolean;
  onBlur?: () => void;
  children: React.ReactNode;
}

function isPortaledSelectMenu(target: EventTarget | null): boolean {
  if (!(target instanceof Node)) return false;
  const element = target instanceof Element ? target : target.parentElement;
  return Boolean(
    element?.closest?.(".cb-select__menu-portal, .rfv2-packing-list-menu-portal"),
  );
}

function AccordionPanelContent({ isExpanded, onBlur, children }: AccordionPanelContentProps) {
  const contentBodyRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const [allowOverflow, setAllowOverflow] = useState(false);

  const isMeasured = contentHeight !== null;

  useLayoutEffect(() => {
    const element = contentBodyRef.current;
    if (!element) return;

    const updateHeight = () => {
      setContentHeight(element.scrollHeight);
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [children]);

  useEffect(() => {
    if (isExpanded) {
      setAllowOverflow(false);
    }
  }, [isExpanded]);

  const handleTransitionEnd = () => {
    if (isExpanded) {
      setAllowOverflow(true);
    } else {
      setAllowOverflow(false);
    }
  };

  const handleFocusOut = (event: React.FocusEvent<HTMLDivElement>) => {
    if (isPortaledSelectMenu(event.relatedTarget)) return;
    const { currentTarget, relatedTarget: relatedTargetRaw } = event;
    const relatedTarget = relatedTargetRaw as Node | null;
    if (relatedTarget && currentTarget.contains(relatedTarget)) return;
    onBlur?.();
  };

  return (
    <div
      className="rfv2-accordion__content-wrapper"
      style={{
        height: isExpanded ? contentHeight ?? 0 : 0,
        overflow: isExpanded && allowOverflow ? "visible" : "hidden",
        transition: isMeasured ? "height 300ms ease" : "none",
      }}
      onTransitionEnd={handleTransitionEnd}
    >
      <div ref={contentBodyRef} className="rfv2-accordion__content-body" onBlur={handleFocusOut}>
        {children}
      </div>
    </div>
  );
}

function ScrollOnExpand({ expanded, targetRef }: { expanded: boolean; targetRef: React.RefObject<HTMLDivElement> }) {
  const wasExpanded = useRef(expanded);

  useEffect(() => {
    if (expanded && !wasExpanded.current) {
      const accordion = targetRef.current?.closest<HTMLElement>(".rfv2-accordion");
      const contentWrapper = accordion?.querySelector<HTMLElement>(".rfv2-accordion__content-wrapper");

      if (!accordion || !contentWrapper) {
        wasExpanded.current = expanded;
        return;
      }

      let rafId: number;
      let done = false;

      const tick = () => {
        if (done) return;
        accordion.scrollIntoView({ behavior: "instant", block: "start" });
        rafId = requestAnimationFrame(tick);
      };

      rafId = requestAnimationFrame(tick);

      const onTransitionEnd = (e: TransitionEvent) => {
        if (e.propertyName !== "height") return;
        done = true;
        cancelAnimationFrame(rafId);
        accordion.scrollIntoView({ behavior: "smooth", block: "start" });
        contentWrapper.removeEventListener("transitionend", onTransitionEnd);
      };

      contentWrapper.addEventListener("transitionend", onTransitionEnd);
      wasExpanded.current = expanded;

      return () => {
        done = true;
        cancelAnimationFrame(rafId);
        contentWrapper.removeEventListener("transitionend", onTransitionEnd);
      };
    }
    wasExpanded.current = expanded;
  }, [expanded, targetRef]);

  return null;
}

function preventInteraction(e: React.SyntheticEvent) {
  e.stopPropagation();
  e.preventDefault();
}

export default function RequestFormAccordion({
  uuid,
  title,
  badge,
  subHeaderBadge,
  disabled,
  onBlur,
  children,
}: RequestFormAccordionProps) {
  const sectionTestId = SECTION_TEST_IDS[uuid];
  const headingRef = useRef<HTMLDivElement>(null);

  return (
    <AccordionItem
      uuid={uuid}
      className={classNames("rfv2-accordion", {
        "rfv2-accordion--disabled": disabled,
        "rfv2-accordion--has-sub-header-badge": Boolean(subHeaderBadge),
      })}
      data-testid={sectionTestId}
    >
      <AccordionItemState>
        {({ expanded }) => <ScrollOnExpand expanded={Boolean(expanded)} targetRef={headingRef} />}
      </AccordionItemState>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        ref={headingRef}
        onClickCapture={disabled ? preventInteraction : undefined}
        onKeyDownCapture={disabled ? preventInteraction : undefined}
      >
        <AccordionItemHeading aria-level={3} className="rfv2-accordion__heading">
          <AccordionItemButton className="rfv2-accordion__header" aria-disabled={disabled || undefined}>
            <div className="rfv2-accordion__header-content">
              <div className="rfv2-accordion__header-row">
                <div className="d-flex align-items-center gap-md">
                  <span className="rfv2-accordion__title">{title}</span>
                  {badge}
                </div>
              </div>
              {subHeaderBadge && <div className="rfv2-accordion__sub-header-badge">{subHeaderBadge}</div>}
            </div>
            <AccordionItemState>
              {({ expanded: isExpanded }) => (
                <ChevronDown
                  className={classNames("rfv2-accordion__chevron", {
                    "rfv2-accordion__chevron--open": isExpanded,
                  })}
                />
              )}
            </AccordionItemState>
          </AccordionItemButton>
        </AccordionItemHeading>
      </div>
      <AccordionItemPanel className="rfv2-accordion__panel">
        <AccordionItemState>
          {({ expanded: isExpanded }) => (
            <AccordionPanelContent isExpanded={Boolean(isExpanded)} onBlur={onBlur}>{children}</AccordionPanelContent>
          )}
        </AccordionItemState>
      </AccordionItemPanel>
    </AccordionItem>
  );
}
